import {
   Badge,
   BadgeProps,
   Box,
   Button,
   type ButtonProps,
   Flex,
   HStack,
   Image,
   ImageProps,
   Link,
   LinkProps,
   Popover,
   PopoverArrow,
   PopoverBody,
   PopoverContent,
   PopoverTrigger,
   Select,
   StackProps,
   Text,
   ThemingProps,
   VStack,
   useBreakpoint,
   useBreakpointValue,
   useSize,
} from '@chakra-ui/react';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { useTranslations } from '@ifixit/i18n';
import { type Money, shouldShowProductRating } from '@ifixit/helpers';
import { FaIcon } from '@ifixit/icons';
import { ___p, _js } from '@ifixit/localize';
import { Rating } from '@ifixit/product-components';
import { type CartLineItem } from '@ifixit/shopify-cart-sdk';
import { useAddToCart } from '@ifixit/shopify-cart-sdk/hooks';
import { ProductVariantPrice } from '@ifixit/ui-chakra';
import trackInPiwikAndGA from 'Shared/Analytics/CombinedGAPiwik';
import { fireGlobal } from 'Shared/utils';
import {
   Dispatch,
   Fragment,
   MutableRefObject,
   PropsWithChildren,
   SetStateAction,
   useLayoutEffect,
   useRef,
   useState,
   type JSX,
} from 'react';
import { useHistoryState } from './HistoryState';
import { PlaceholderImage } from './PlaceholderImage';
import { QuantityBadge } from './QuantityBadge';
import { LocaleCode } from '@ifixit/i18n';

export interface GuideProductsData {
   kits: GuideProduct[];
   parts: GuideProduct[];
   tools: GuideProduct[];
}

interface GuideProductVariant {
   name: string;
   proPricesByTier: Record<string, Money>;
   totalPrice: string;
   cartLine: CartLineItem;
   isInStock: boolean;
}

export interface GuideProduct {
   name: string;
   sku: string | null;
   totalPrice: string | null;
   quantity: number;
   notes: string | null;
   type: string | null;
   productUrl: string;
   wikiUrl: string;
   imageUrl: string | null;
   highResImageUrl: string | null;
   isOptional: boolean;
   isExternal: boolean;
   isForSale: boolean;
   supplierText: string | null;
   rating: number | null;
   ratingCount: number;
   variants: GuideProductVariant[];
   initialVariant: GuideProductVariant | null;
   localeCode: LocaleCode;
}
type PartOrTool = 'Part' | 'Tool' | 'Kit';
type ButtonVariant = 'cta' | 'secondary';

function ProductItem({
   name,
   sku,
   quantity,
   notes,
   type,
   productUrl,
   imageUrl,
   highResImageUrl,
   isExternal,
   isForSale,
   supplierText,
   rating,
   ratingCount,
   variants,
   initialVariant,
   localeCode,
   addToCartText,
   viewProductText,
   wrapperStyling,
   imageStyling,
   partOrTool,
   isOptional,
   buttonVariant,
}: GuideProduct & {
   addToCartText: string;
   viewProductText: string;
   wrapperStyling: StackProps;
   imageStyling: ImageProps;
   partOrTool: PartOrTool;
   buttonVariant: ButtonVariant;
}) {
   const openInNewTab = true;
   const breakpoint = useBreakpoint({ ssr: false });
   const isTablet = breakpoint == 'sm' || breakpoint == 'base';
   const [selectedVariant, setSelectedVariant] = useState<GuideProductVariant | null>(
      initialVariant
   );
   const url = selectedVariant?.cartLine.url ?? productUrl;

   const title = type ? `${name} (${type})` : name;
   const escapedDashTitle = title.replace('-', '\u2011');

   const onProductClick = () => {
      trackInPiwikAndGA({
         eventCategory: 'Guide Products',
         eventAction: `Guide Products - Click Product - ${partOrTool}${
            isExternal ? ' - External' : ''
         }`,
         eventName: isExternal ? `external:${name}:${url}` : `ifixit:${name}:${sku}`,
      });
   };

   return (
      <VStack
         as="li"
         width="100%"
         align="stretch"
         p={3}
         {...wrapperStyling}
         onClick={onProductClick}
      >
         <HStack align="stretch" flexShrink={0}>
            <Box position="relative" marginRight={1.5} display="flex" alignSelf="flex-start">
               <Link
                  href={url}
                  isExternal={openInNewTab}
                  alignSelf="flex-start"
                  display="flex"
                  _visited={{ color: 'gray.700' }}
                  sx={{
                     '&:hover img': {
                        borderWidth: '1px',
                        borderColor: 'gray.300',
                        borderRadius: '4px',
                     },
                  }}
               >
                  {imageUrl ? (
                     <Image
                        alignSelf="flex-start"
                        borderWidth={1}
                        borderColor="gray.300"
                        borderRadius="4px"
                        display="block"
                        src={imageUrl}
                        srcSet={`${imageUrl} 1x, ${highResImageUrl} 2x`}
                        objectFit="cover"
                        alt={name}
                        {...imageStyling}
                     />
                  ) : (
                     <PlaceholderImage {...imageStyling} />
                  )}
                  {quantity > 1 && <QuantityBadge quantity={quantity} />}
               </Link>
            </Box>
            <VStack spacing="1" flex={1} alignItems="flex-start">
               <Link
                  fontSize="md"
                  fontWeight="semibold"
                  href={url}
                  isExternal={openInNewTab}
                  className="title"
                  color="gray.900"
                  _visited={{ color: 'gray.900' }}
                  _hover={{ color: 'gray.700', textDecoration: 'none' }}
                  _focus={{ color: 'gray.700', textDecoration: 'none' }}
               >
                  {escapedDashTitle}
               </Link>
               <VariantSelector
                  variants={variants}
                  selected={selectedVariant}
                  setSelected={setSelectedVariant}
               />
               {selectedVariant && isForSale && variants.length < 2 && (
                  <Link href={url} isExternal={openInNewTab} _hover={{ textDecoration: 'none' }}>
                     <ProductVariantPrice
                        price={selectedVariant.cartLine.price}
                        compareAtPrice={selectedVariant.cartLine.compareAtPrice}
                        proPricesByTier={selectedVariant.proPricesByTier}
                        showDiscountLabel={false}
                        size="extra-small"
                        variant="subdued"
                        localeCode={localeCode}
                     />
                  </Link>
               )}
               {rating != null && <ProductReviews rating={rating} count={ratingCount} />}
               <ProductBadges
                  isOptional={isOptional}
                  isExternal={isExternal}
                  supplierText={supplierText}
                  url={url}
                  openInNewTab={openInNewTab}
               />
               {isExternal && supplierText && (
                  <Link
                     fontSize="md"
                     color="amber.600"
                     href={url}
                     isExternal={openInNewTab}
                     _visited={{ color: 'amber.600' }}
                  >
                     {supplierText}
                  </Link>
               )}
               {notes && (
                  <Link
                     fontSize="sm"
                     href={url}
                     isExternal={openInNewTab}
                     _visited={{ color: 'gray.700' }}
                  >
                     {notes}
                  </Link>
               )}
            </VStack>
         </HStack>
         <Flex justifyContent="flex-end" className="buy-buttons">
            <BuyButton
               buttonSize={isTablet ? 'sm' : 'md'}
               isForSale={isForSale}
               isExternal={isExternal}
               openInNewTab={openInNewTab}
               productUrl={url}
               addToCartText={addToCartText}
               viewProductText={viewProductText}
               partOrTool={partOrTool}
               cartLine={selectedVariant?.cartLine ?? null}
               isInStock={selectedVariant?.isInStock ?? false}
               localeCode={localeCode}
               buttonVariant={buttonVariant}
            />
         </Flex>
      </VStack>
   );
}

function VariantSelector({
   variants,
   selected,
   setSelected,
}: {
   variants: GuideProductVariant[];
   selected: GuideProductVariant | null;
   setSelected: (_: GuideProductVariant | null) => void;
}) {
   if (variants.length < 2 || !selected) {
      return null;
   }
   const inStockVariants = variants.filter(v => v.isInStock);
   const outOfStockVariants = variants.filter(v => !v.isInStock);
   const soldOut = _js('Sold out');
   return (
      <Select
         value={selected.name}
         onChange={event => {
            const newSelection = inStockVariants.find(
               variant => variant.name === event.target.value
            );
            setSelected(newSelection ?? null);
         }}
         overflow="hidden"
         textOverflow="ellipsis"
         backgroundColor="white"
         borderColor="gray.200"
      >
         {inStockVariants.map(variant => (
            <option key={variant.name} value={variant.name} disabled={false}>
               {variant.totalPrice} {variant.name}
            </option>
         ))}
         {outOfStockVariants.map(variant => (
            <option key={variant.name} value={variant.name} disabled={true}>
               {variant.totalPrice} {variant.name} ({soldOut})
            </option>
         ))}
      </Select>
   );
}

type ProductReviewsProps = {
   rating: NonNullable<GuideProduct['rating']>;
   count: GuideProduct['ratingCount'];
};
function ProductReviews({ rating, count }: ProductReviewsProps) {
   const t = useTranslations('ProductPage');
   if (!shouldShowProductRating({ rating, count })) {
      return null;
   }
   return (
      <HStack>
         <Rating value={rating} ariaLabel={t('sections.reviews.ratingStars', { rating })} />
         <Text color="gray.600" aria-hidden mt="1">
            {rating}
         </Text>
      </HStack>
   );
}

function ProductBadges({
   isOptional,
   isExternal,
   supplierText,
   url,
   openInNewTab,
}: {
   isOptional: boolean;
   isExternal: boolean;
   supplierText: string | null;
   url: string;
   openInNewTab: boolean;
}) {
   const hasAffiliateLink =
      isExternal &&
      supplierText &&
      (supplierText.includes('Ebay') || supplierText.includes('Amazon'));
   const hasBadge = isOptional || hasAffiliateLink;
   if (!hasBadge) {
      return null;
   }

   return (
      <Flex flexWrap="wrap" gap="1.5">
         {isOptional && <OptionalBadge url={url} openInNewTab={openInNewTab} />}
         {hasAffiliateLink && <AffiliateBadge />}
      </Flex>
   );
}

function AffiliateBadge() {
   return (
      <Popover trigger="hover">
         <PopoverTrigger>
            <ProductBadge color="gray" cursor="default">
               <FaIcon icon={faCircleInfo} color="gray.500" paddingRight={1} fontSize="10px" />
               {_js('Affiliate link')}
            </ProductBadge>
         </PopoverTrigger>
         <PopoverContent maxWidth="fit-content">
            <PopoverArrow backgroundColor="gray.300" />
            <PopoverBody
               fontSize="14px"
               fontWeight="400"
               lineHeight="14px"
               textTransform="none"
               background="gray.100"
               color="gray.700"
               borderWidth={1}
               borderColor="gray.300"
               borderRadius="md"
               padding={4}
            >
               {_js('iFixit earns commission when you buy through these links.')}
            </PopoverBody>
         </PopoverContent>
      </Popover>
   );
}

function OptionalBadge({
   url,
   openInNewTab,
}: {
   url: string;
   openInNewTab: boolean;
}) {
   return (
      <ProductBadge
         color="amber"
         as={Link}
         href={url}
         isExternal={openInNewTab}
         _visited={{ color: 'amber.700' }}
      >
         <FaIcon icon={faCircleInfo} color={'amber.500'} paddingRight={1} fontSize="10px" />
         {_js('Optional')}
      </ProductBadge>
   );
}

const ProductBadge = ({
   children,
   color,
   ...props
}: PropsWithChildren<BadgeProps & LinkProps & { color: string }>) => (
   <Badge
      colorScheme={color}
      borderWidth={1}
      borderColor="gray.300"
      display="flex"
      alignItems="center"
      fontSize="10px"
      fontWeight="semibold"
      lineHeight="12px"
      textTransform="none"
      borderRadius="md"
      padding="4px 6px"
      {...props}
   >
      {children}
   </Badge>
);

function GuideItemMicrodata({
   partOrTool,
   name,
   quantity,
   productUrl,
   totalPrice,
}: Pick<GuideProduct, 'name' | 'quantity' | 'productUrl' | 'totalPrice'> & {
   partOrTool: PartOrTool;
}) {
   if (partOrTool === 'Part' || partOrTool === 'Kit') {
      return (
         <PartSchema
            name={name}
            quantity={quantity}
            productUrl={productUrl}
            totalPrice={totalPrice}
         />
      );
   }
   return <ToolSchema name={name} quantity={quantity} productUrl={productUrl} />;
}

function PartSchema({
   name,
   quantity,
   productUrl,
   totalPrice,
}: Pick<GuideProduct, 'name' | 'quantity' | 'productUrl' | 'totalPrice'>) {
   return (
      <div itemProp="supply" itemScope itemType="http://schema.org/HowToSupply">
         <meta itemProp="name" content={name} />
         <meta itemProp="requiredQuantity" content={`${quantity}`} />
         <meta itemProp="url" content={productUrl} />
         <meta itemProp="estimatedCost" content={`${totalPrice}`} />
      </div>
   );
}

function ToolSchema({
   quantity,
   productUrl,
   name,
}: Pick<GuideProduct, 'name' | 'quantity' | 'productUrl'>) {
   return (
      <div itemProp="tool" itemScope itemType="http://schema.org/HowToTool">
         <meta itemProp="name" content={name} />
         <meta itemProp="requiredQuantity" content={`${quantity}`} />
         <meta itemProp="url" content={productUrl} />
      </div>
   );
}

function BuyButton({
   isForSale,
   productUrl: url,
   addToCartText,
   viewProductText,
   buttonSize,
   openInNewTab,
   cartLine,
   isInStock,
   localeCode,
   buttonVariant,
}: Pick<GuideProduct, 'isForSale' | 'productUrl' | 'isExternal'> & {
   addToCartText: string;
   viewProductText: string;
   buttonSize: ThemingProps<'Button'>['size'];
   partOrTool: PartOrTool;
   openInNewTab: boolean;
   cartLine: CartLineItem | null;
   isInStock: boolean;
   localeCode: LocaleCode;
   buttonVariant: ButtonVariant;
}) {
   if (!isForSale) {
      return null;
   }
   const openSettings = openInNewTab ? { target: '_blank', rel: 'noopener' } : {};
   const buyButtonProps = {
      ...openSettings,
      size: buttonSize,
      variant: buttonVariant,
      borderWidth: '0',
      fontWeight: 'normal',
      py: 0,
      px: 3,
      height: 'auto',
      minHeight: '32px',
      alignSelf: { md: 'flex-start', sm: 'flex-end', base: 'flex-end' },
      flexShrink: 0,
   };

   if (cartLine && isInStock) {
      return (
         <AddToCartButton
            addToCartText={addToCartText}
            buyButtonProps={buyButtonProps}
            cartLine={cartLine}
            localeCode={localeCode}
         />
      );
   }

   return (
      <Button as="a" href={url} {...buyButtonProps}>
         {viewProductText}
      </Button>
   );
}

function AddToCartButton({
   addToCartText,
   cartLine,
   localeCode,
   buyButtonProps,
}: {
   addToCartText: string;
   buyButtonProps: ButtonProps;
   cartLine: CartLineItem;
   localeCode: LocaleCode;
}) {
   const { addToCart, enabled } = useAddToCart();
   return (
      <Button
         isDisabled={!enabled}
         onClick={() => {
            addToCart.mutate({
               analytics: {
                  descriptor: 'Guide Products',
                  localeCode: localeCode,
               },
               lines: [cartLine],
            });
            fireGlobal('cart.addcomplete');
         }}
         {...buyButtonProps}
      >
         {addToCartText}
      </Button>
   );
}

function FeaturedProduct(props: GuideProduct & { partOrTool: PartOrTool }) {
   const border = {
      borderTop: '1px solid',
      borderLeft: '1px solid',
      borderRight: '1px solid',
      borderColor: 'brand.200',
   };

   const styling = {
      wrapperStyling: {
         bg: 'brand.50',
         _first: { borderTopRadius: '4px', ...border },
         _notLast: border,
         _last: {
            borderBottomRadius: '4px',
            borderBottomWidth: '1px',
            ...border,
         },
      },
      imageStyling: {
         height: '64px',
         width: '64px',
      },
      buttonVariant: 'cta' as ButtonVariant,
   };
   return (
      <ProductItem
         {...props}
         {...styling}
         addToCartText={_js('Add to cart')}
         viewProductText={_js('View')}
         buttonVariant={styling.buttonVariant}
      />
   );
}

function Product(props: GuideProduct & { partOrTool: PartOrTool }) {
   const border = {
      borderTopWidth: '1px',
      borderLeftWidth: '1px',
      borderRightWidth: '1px',
      borderColor: 'gray.200',
   };

   const styling = {
      wrapperStyling: {
         bg: 'white',
         _first: { borderTopRadius: '4px', ...border },
         _notLast: border,
         _last: {
            borderBottomRadius: '4px',
            borderBottomWidth: '1px',
            ...border,
         },
      },
      imageStyling: {
         height: '48px',
         width: '48px',
      },
      buttonVariant: 'cta' as ButtonVariant,
   };

   return (
      <ProductItem
         {...props}
         {...styling}
         addToCartText={props.isExternal ? _js('View') : _js('Add to cart')}
         viewProductText={_js('View')}
         buttonVariant={styling.buttonVariant}
      />
   );
}

function ProductList({
   products,
   heading,
   subheading,
   ItemsAs,
   partOrTool,
   renderMicrodata,
}: {
   products: Array<GuideProduct>;
   heading?: string;
   subheading?: string;
   ItemsAs: (_: GuideProduct & { partOrTool: PartOrTool }) => JSX.Element;
   partOrTool: PartOrTool;
   renderMicrodata: boolean;
}) {
   const productItems = products.map((product: GuideProduct, index) => (
      <Fragment key={index}>
         <ItemsAs {...product} partOrTool={partOrTool} />
      </Fragment>
   ));

   const ItemsMicrodata = renderMicrodata
      ? products.map((product: GuideProduct, index) => (
           <GuideItemMicrodata
              key={index}
              name={product.name}
              quantity={product.quantity}
              productUrl={product.productUrl}
              totalPrice={product.totalPrice}
              partOrTool={partOrTool}
           />
        ))
      : null;

   return (
      <>
         {heading && (
            <Text
               color="gray.800"
               fontSize={{ base: 'lg', sm: 'xl' }}
               fontWeight="medium"
               padding="0"
               margin="0"
               _notFirst={{ paddingTop: '16px' }}
            >
               {heading}
            </Text>
         )}
         {subheading && (
            <Text fontSize="md" fontWeight="400" padding="0" margin="0" color="gray.500">
               {subheading}
            </Text>
         )}
         <Flex flexDirection="row" flexShrink={0} width="100%">
            <VStack
               as="ul"
               className={`item-list-${partOrTool.toLowerCase()}s`}
               spacing="0"
               padding="0"
               paddingTop="12px"
               width="100%"
               flexShrink={0}
               _notLast={{ paddingBottom: '12px' }}
               margin={0}
               overflowWrap="break-word"
            >
               {productItems}
            </VStack>
         </Flex>
         {ItemsMicrodata}
      </>
   );
}

export function GuideProductsSection({
   kits,
   tools,
   parts,
   introRef,
}: GuideProductsData & { introRef: MutableRefObject<HTMLDivElement> }) {
   const breakpoint = useBreakpoint({ ssr: false });
   const isMobile = breakpoint == 'base';

   const sizeDims = useSize(introRef);

   const heightDiff = sizeDims?.height == null ? 0 : sizeDims.height;
   const sectionCollapsedHeight = isMobile ? 264 : Math.max(heightDiff, 370);

   const buttonHeight = 80;
   const padding = useBreakpointValue({ base: 12, md: 16 });

   const [isExpanded, setIsExpanded] = useHistoryState(false, 'productsExpanded');
   const elementRef = useRef<HTMLDivElement>(undefined);

   useLayoutEffect(() => {
      if (!elementRef.current) {
         return;
      }

      const myHeight = elementRef.current.scrollHeight;
      const expandedByDefault = myHeight < sectionCollapsedHeight + buttonHeight;
      if (expandedByDefault) {
         setIsExpanded(true);
      }
   }, [elementRef]);

   const border = { borderWidth: '1px', borderColor: 'gray.300' };

   const hasKit = kits.length > 0;

   return (
      <VStack
         spacing="0"
         padding={`${padding}px`}
         paddingBottom={isExpanded ? `${padding}px` : '0px'}
         flexGrow={0}
         alignItems="flex-start"
         {...border}
         borderRadius="4px"
         overflowY={{ base: 'hidden', md: 'auto' }}
         overflowX="hidden"
         transitionProperty="max-height"
         // @ts-expect-error TS(2322) FIXME: Type 'string | null' is not assignable to type 'Re... Remove this comment to see the full error message
         transitionDuration={isExpanded ? '300ms' : null}
         maxHeight={isExpanded ? '100%' : `${sectionCollapsedHeight}px`}
         // @ts-expect-error TS(2322) FIXME: Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message
         ref={elementRef}
      >
         {kits.length && (
            <ProductList
               products={kits}
               heading={___p(kits.length, 'Fix Kit', 'Fix Kits')}
               ItemsAs={FeaturedProduct}
               partOrTool="Kit"
               subheading={___p(
                  kits.length,
                  'This kit contains all the parts and tools needed to complete this guide.',
                  'These kits contain all the parts and tools needed to complete this guide.'
               )}
               renderMicrodata={true}
            />
         )}
         {parts.length && (
            <ProductList
               products={parts}
               heading={_js('Parts')}
               ItemsAs={Product}
               partOrTool="Part"
               renderMicrodata={!hasKit}
            />
         )}
         {tools.length && (
            <ProductList
               products={tools}
               heading={_js('Tools')}
               ItemsAs={Product}
               partOrTool="Tool"
               renderMicrodata={!hasKit}
            />
         )}
         <ExpandCollapse
            // @ts-expect-error TS(2322) FIXME: Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
            parentPadding={padding}
            buttonHeight={buttonHeight}
            isExpanded={isExpanded}
            // @ts-expect-error TS(2322) FIXME: Type 'Dispatch<boolean>' is not assignable to type... Remove this comment to see the full error message
            setIsExpanded={setIsExpanded}
         />
      </VStack>
   );
}

function ExpandCollapse({
   buttonHeight,
   parentPadding,
   isExpanded,
   setIsExpanded,
}: {
   buttonHeight: number;
   parentPadding: number;
   isExpanded: boolean;
   setIsExpanded: Dispatch<SetStateAction<boolean>>;
}) {
   const onClick = () => {
      trackInPiwikAndGA({
         eventCategory: 'Guide Products',
         eventAction: 'Guide Products - Show more',
      });
      setIsExpanded(true);
   };

   if (isExpanded) {
      return null;
   }

   return (
      <Box
         position="sticky"
         bottom="0px"
         width={`calc(100% + 2 * ${parentPadding}px)`}
         minHeight={`${buttonHeight}px`}
         borderBottomRadius="4px"
         style={{
            marginRight: `-${parentPadding}px`,
            marginLeft: `-${parentPadding}px`,
            marginTop: '1px',
         }}
         padding="0"
         background={
            'linear-gradient(180deg, rgba(255, 255, 255, 0) 0, rgba(255, 255, 255, 1) 40%, #FFFFFF 100%)'
         }
         _focus={{
            textDecoration: 'underline',
         }}
         flexDirection="column"
         justifyContent="flex-end"
         display="flex"
         alignItems="center"
         pointerEvents="none"
      >
         <Text
            fontSize="md"
            color="brand.500"
            fontWeight="medium"
            onClick={onClick}
            cursor="pointer"
            margin={0}
            textAlign="center"
            width="100%"
            py={3}
            pointerEvents="all"
         >
            {_js('Show more…')}
         </Text>
      </Box>
   );
}
